import React from "react";
import classNames from "classnames";
import { useModal } from "@root/client/modal";
import { project } from "@planzone/dto";
import { UseCases } from "@planzone/core";
import { Safeguard } from "@scenes/safeguard";
import { ResourceAvatar } from "@common/resourceAvatar";
import { Controls, useToggle } from "@common/treeToggle";
import { AutoFocusContext } from "../autoFocusCtx";
import { ListToggleArrow } from "@common/listToggleArrow";
import { RecordList } from "./recordList";
import { Props } from "./types";
import { Project } from "./project";
import styles from "./styles.scss";

function Root({
  timeSheet: { projects, resource, timesheetDeadline },
  areDetailsDisplayed,
  filter,
  showDeactivatedProjects,
  isReloading,
  showCompletionColumn,
  isOpenByDefault = true,
  showControls,
}: Props) {
  const { pushView } = useModal();
  const { isOpen, toggle } = useToggle(resource.uuid, 0, isOpenByDefault);
  const { lastAllocatedResourceUuid } = React.useContext(AutoFocusContext);

  React.useEffect(() => {
    if (lastAllocatedResourceUuid === resource.uuid && !isOpen) {
      toggle();
    }
  }, [lastAllocatedResourceUuid]);

  return (
    <Safeguard useCases={[UseCases.retrieveResource]}>
      {({ canRetrieveResource }) => (
        <div className={styles.allocation}>
          <div className={styles.main}>
            <div className={styles.resource}>
              <div className={styles.iconBox} onClick={toggle}>
                <ListToggleArrow isOpen={isOpen} />
              </div>
              <div
                className={styles.row}
                onClick={() =>
                  canRetrieveResource &&
                  pushView({
                    key: UseCases.retrieveResource,
                    data: resource.uuid,
                  })
                }
              >
                <div className={styles.avatar}>
                  <ResourceAvatar resource={resource} />
                </div>
                <div className={styles.col}>
                  <p className={styles.name}>{resource.name}</p>
                  {resource.profession && (
                    <p className={styles.profession}>{resource.profession}</p>
                  )}
                </div>
              </div>
              {showControls && (
                <div className={styles.controls}>
                  <Controls />
                </div>
              )}
            </div>
            <div className={styles.recordList}>
              <RecordList
                {...resource}
                type="resource"
                resource={resource}
                project={project}
                records={[]}
                allocatedHours={resource.totalOfAllocatedHours}
                spentTime={resource.totalOfSpentTime}
                spentTimeInWeek={resource.totalOfSpentTimeInWeek}
                remainingTime={resource.totalOfRemainingTime}
                areDetailsDisplayed={areDetailsDisplayed}
                progression={resource.percentCompletion}
                isReloading={isReloading}
                showCompletionColumn={showCompletionColumn}
              />
            </div>
          </div>
          {isOpen && (
            <div className={styles.projects}>
              {projects
                .filter(
                  (project) =>
                    !showDeactivatedProjects || !project.memberDeactivated,
                )
                .map((project) => (
                  <Project
                    key={project.uuid}
                    project={project}
                    resource={resource}
                    filter={filter}
                    areDetailsDisplayed={areDetailsDisplayed}
                    isReloading={isReloading}
                    showCompletionColumn={showCompletionColumn}
                    timesheetDeadline={timesheetDeadline}
                  />
                ))}
            </div>
          )}
        </div>
      )}
    </Safeguard>
  );
}

export { Root };
